import React, { FC, useEffect, useMemo } from 'react';

import { ArrowBack } from '@mui/icons-material';
import { Box, Button, Container, Typography, Alert, AlertTitle } from '@mui/material';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router';
import shallow from 'zustand/shallow';

import { Card } from '../../components/atoms/Card';
import { InputRadio } from '../../components/molecules/InputRadio';
import { SubmissionStatusChip } from '../../components/molecules/SubmissionStatusChip';
import { CentralisedLoading } from '../../components/utils/CentralisedLoading';
import { QuillEditor } from '../../components/utils/quill-editor';
import { useAnalytics } from '../../hooks/dataTracking/useAnalytics';
import { useErrorToast } from '../../hooks/useErrorToast';
import { ESubmissionStatus } from '../../interfaces/submission';
import { useAuthStore } from '../../stores/auth';
import { useSingleSubmissionStore } from '../../stores/singleSubmission';

export const MyProposalPage: FC = () => {
  const params = useParams();
  const { id } = params;
  const navigate = useNavigate();

  const { sendButtonClickTrackingEvent } = useAnalytics();

  const storedToken = useAuthStore((store) => store.storedToken);

  const { submission, loading, error, getSingleSubmission, clearSingleSubmission } = useSingleSubmissionStore(
    ({ submission, loading, error, getSingleSubmission, clearSingleSubmission }) => ({
      submission,
      loading,
      error,
      getSingleSubmission,
      clearSingleSubmission,
    }),
    shallow,
  );
  useErrorToast(error);

  const { _id, title, status, summary, problem, appetite, solution, rabbitHoles, noGos, voteOptions, archiveReason } =
    submission || {};

  const nextVoteOptions = useMemo(
    () => voteOptions?.map((option) => ({ title: option, value: option })) || [],
    [voteOptions],
  );

  useEffect(() => {
    if (id) getSingleSubmission({ id: id, accessToken: storedToken });
  }, [id]);

  useEffect(() => {
    // Clear Proposal and Error on Dismount
    // Do that previous submission will not show when navigating back to page
    return () => {
      clearSingleSubmission();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 7,
        }}
      >
        {loading ? (
          <CentralisedLoading sx={{ mt: 6 }} />
        ) : (
          <Container maxWidth={'lg'} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ width: '100%', pr: { xs: 0, md: 4 } }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => {
                  navigate(-1);
                  sendButtonClickTrackingEvent('Navigate back to proposal list page from my proposal page');
                }}
                sx={{ mb: 3 }}
              >
                Back
              </Button>
              {submission && (
                <>
                  {status == ESubmissionStatus.Declined && archiveReason && (
                    <Alert variant="filled" severity="error" sx={{ mb: 3 }}>
                      <AlertTitle>Declined reason</AlertTitle>
                      {archiveReason}
                    </Alert>
                  )}
                  <Typography variant="h3" sx={{ mb: 0.5 }}>
                    {title}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {status && <SubmissionStatusChip status={status} sx={{ mr: { xs: 0.3, sm: 1 } }} />}
                    </Box>
                  </Box>

                  {/* Beginning of Main Section of Proposal */}
                  <Typography variant="h4" sx={{ mb: 2, mt: 3 }}>
                    Summary
                  </Typography>
                  <QuillEditor readOnly value={summary} />

                  <Typography variant="h4" sx={{ mb: 2, mt: 3 }}>
                    Problem
                  </Typography>
                  <QuillEditor readOnly value={problem} />

                  <Typography variant="h4" sx={{ mb: 2, mt: 3 }}>
                    Appetite
                  </Typography>
                  <QuillEditor readOnly value={appetite} />

                  <Typography variant="h4" sx={{ mb: 2, mt: 3 }}>
                    Solution
                  </Typography>
                  <QuillEditor readOnly value={solution} />

                  <Typography variant="h4" sx={{ mb: 2, mt: 3 }}>
                    Rabbit Holes
                  </Typography>
                  <QuillEditor readOnly value={rabbitHoles} />

                  <Typography variant="h4" sx={{ mb: 2, mt: 3 }}>
                    No-gos
                  </Typography>
                  <QuillEditor readOnly value={noGos} />
                  <Card titleBoxColor="blue" title="Voting Options" sx={{ mt: 5 }}>
                    <InputRadio options={nextVoteOptions} disabled value="" handleChange={(e) => console.log(e)} />
                  </Card>
                </>
              )}
            </Box>
          </Container>
        )}
      </Box>
    </>
  );
};
